import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ExperimentDetail, ExperimentSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { breadcrumbJsonLd, experimentJsonLd } from '@/lib/structured-data'
import { getExperiment, getExperimentSlugs, getRelatedExperiments } from '@/lib/content/lab'
import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import Breadcrumb from '@/components/elements/breadcrumb'
import SampleNotice from '@/components/elements/sample-notice'
import TextLink from '@/components/elements/text-link'
import DisplayHeading from '@/components/typography/display-heading'
import ExperimentStatus from '@/components/lab/experiment-status'
import ExperimentMetadata from '@/components/lab/experiment-metadata'
import ExperimentItem from '@/components/lab/experiment-item'
import ArchitectureBlock from '@/components/lab/architecture-block'
import TableOfContents from '@/components/content/table-of-contents'
import Prose from '@/components/content/prose'

interface ExperimentPageProps {
    experiment: ExperimentDetail
    related: Array<ExperimentSummary>
}

const ExperimentPage: FC<ExperimentPageProps> = ({ experiment, related }): ReactNode => {
    const path = `/lab/${experiment.slug}`

    return (
        <Layout>
            <Seo
                title={experiment.title}
                description={experiment.description}
                path={path}
                type='article'
                publishedTime={experiment.date}
                modifiedTime={experiment.updatedAt ?? undefined}
                tags={experiment.technologies}
                jsonLd={[
                    experimentJsonLd(experiment),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'AI Lab', path: '/lab' },
                        { name: experiment.title, path }
                    ])
                ]}
            />

            <article>
                <header className='tone-ink'>
                    <MainContainer className='pt-10 pb-14 md:pt-14 md:pb-20'>
                        <Breadcrumb
                            items={[
                                { label: 'AI Lab', href: '/lab' },
                                { label: experiment.categoryLabel, href: `/lab?category=${experiment.category}` },
                                { label: experiment.title }
                            ]}
                        />

                        <div className='mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 md:mt-14'>
                            <span className='font-mono text-label uppercase text-foreground'>{experiment.code}</span>
                            <span aria-hidden='true' className='h-px w-6 bg-border-strong' />
                            <ExperimentStatus status={experiment.status} />
                            <span aria-hidden='true' className='text-border-strong'>/</span>
                            <span className='font-mono text-label uppercase text-muted-foreground'>{experiment.categoryLabel}</span>
                        </div>

                        <DisplayHeading as='h1' size='headline' className='mt-6 max-w-[20ch] animate-in fade-in slide-in-from-bottom-2 duration-700 ease-out'>
                            {experiment.title}
                        </DisplayHeading>

                        <p className='mt-6 max-w-[56ch] text-lede text-muted-foreground'>
                            {experiment.description}
                        </p>
                    </MainContainer>
                </header>

                <MainContainer className='pt-10 pb-20 md:pt-14 md:pb-28'>
                    {experiment.sample && (
                        <SampleNotice kind='lab entry' file={`content/lab/${experiment.slug}.md`} className='mb-10 md:mb-14' />
                    )}

                    <EditorialGrid className='gap-y-12'>
                        <aside className='col-span-4 flex flex-col gap-10 md:col-span-12 lg:col-span-3'>
                            <ExperimentMetadata experiment={experiment} />
                            <TableOfContents items={experiment.toc} title='Sections' className='lg:sticky lg:top-28' />
                        </aside>

                        <div className='col-span-4 flex flex-col gap-12 md:col-span-12 lg:col-span-8 lg:col-start-5'>
                            {experiment.schematic && (
                                <ArchitectureBlock
                                    schematic={experiment.schematic}
                                    id={`lab-${experiment.slug}`}
                                    figure='01'
                                    title='Architecture'
                                />
                            )}

                            <Prose html={experiment.html} className='max-w-reading' />

                            {experiment.references.length > 0 && (
                                <section aria-labelledby='references' className='max-w-reading border-t border-foreground pt-4'>
                                    <h2 id='references' className='font-mono text-label uppercase text-muted-foreground'>
                                        Source code and references
                                    </h2>

                                    <ol className='mt-4 flex flex-col'>
                                        {experiment.references.map((reference, index) => (
                                            <li key={reference.url} className='flex items-baseline gap-4 border-b border-border py-3'>
                                                <span className='font-mono text-caption text-muted-foreground'>
                                                    [{index + 1}]
                                                </span>
                                                <TextLink href={reference.url} arrow>{reference.label}</TextLink>
                                            </li>
                                        ))}
                                    </ol>
                                </section>
                            )}
                        </div>
                    </EditorialGrid>
                </MainContainer>
            </article>

            {related.length > 0 && (
                <MainContainer as='section' className='pb-24 md:pb-32'>
                    <div className='flex items-center justify-between gap-4 border-b border-foreground pb-4'>
                        <h2 className='flex items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                            <span aria-hidden='true' className='size-1.5 bg-brand' />
                            More from the lab
                        </h2>

                        <TextLink href='/lab' arrow mono>Full log</TextLink>
                    </div>

                    <ul aria-label='Related experiments'>
                        {related.map((item) => (
                            <ExperimentItem key={item.slug} experiment={item} compact />
                        ))}
                    </ul>
                </MainContainer>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: getExperimentSlugs().map((slug) => ({ params: { slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<ExperimentPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const experiment = await getExperiment(slug)

    if (!experiment) return { notFound: true }

    return {
        props: {
            experiment,
            related: getRelatedExperiments(slug, 3)
        }
    }
}

export default ExperimentPage
